import React from 'react';
import { Table, Button, Input, Space, Popconfirm, Modal, Form, Select, Tag, Tooltip } from 'antd';
import { SearchOutlined, TagsOutlined, LockOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { deleteTag, createTags as createVendorTags, updateTagName, fetchVendorTags } from '../../actions/tagsActions';


export default function Tags() {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.tags.items);
  const tagsLoading = useSelector((state) => state.tags.loading);
  const total = useSelector((state) => state.tags.total);
  const page = useSelector((state) => state.tags.page);
  const perPage = useSelector((state) => state.tags.perPage);
  const [newFormVisible, setNewFormVisible] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [form] = Form.useForm();
  const [editingId, setEditingId] = React.useState(null);

  const load = React.useCallback((nextPage = page, nextPer = perPage, q = query) => {
    dispatch(fetchVendorTags(nextPage, nextPer, q));
  }, [dispatch, page, perPage, query]);

  React.useEffect(() => { load(); }, [load]);

  const handleBulkCreate = (values) => {
    const list = Array.isArray(values.tag_list) ? values.tag_list.map((t) => String(t).trim()).filter(Boolean) : [];
    if (list.length === 0) {
      setNewFormVisible(false);
      return;
    }
    dispatch(createVendorTags(list));
    setNewFormVisible(false);
    form.resetFields();
  };

  const handleUpdate = (id, name) => {
    dispatch(updateTagName(id, name));
  };

  const handleDelete = (id) => {
    dispatch(deleteTag(id));
  };

  const columns = [
    {
      title: 'Tag',
      dataIndex: 'name',
      render: (text, record) => (
        <InlineEditable
          text={text}
          isSystem={record.system}
          editing={editingId === record.id}
          onSave={(val) => { handleUpdate(record.id, val); setEditingId(null); }}
        />
      ),
      ellipsis: true
    },
    {
      title: 'Action',
      align: 'right',
      width: 180,
      render: (_, record) => (
        record.system ? (
          <Tooltip title="System tag can't be edited or deleted">
            <LockOutlined style={{ color: 'rgba(0,0,0,0.35)' }} />
          </Tooltip>
        ) : (
          <Space>
            <Button type="link" onClick={() => setEditingId(record.id)}>Edit</Button>
            <Popconfirm title="Delete tag?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
              <Button danger type="link">Delete</Button>
            </Popconfirm>
          </Space>
        )
      )
    }
  ];

  const filtered = React.useMemo(() => {
    const list = Array.isArray(items) ? [...items] : [];
    return list.sort((a, b) => {
      if ((a.system || false) !== (b.system || false)) return a.system ? -1 : 1; // System first
      const an = String(a.name || '').toLowerCase();
      const bn = String(b.name || '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, [items]);

  return (
    <div style={{ background: '#fff', padding: 16, width: '100%' }}>
      <div className="button-action-bar" style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-start', alignItems: 'center' }}>
        <Input
          placeholder="Search by name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          suffix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          allowClear
          style={{ width: 360 }}
        />
        <Space>
          <Button type="primary" icon={<TagsOutlined />} onClick={() => setNewFormVisible(true)}>
            Add Tags
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        loading={tagsLoading}
        pagination={{ current: page, total, pageSize: perPage, onChange: (p, ps) => load(p, ps) }}
      />

      <Modal
        title="Create Tags"
        visible={newFormVisible}
        onCancel={() => setNewFormVisible(false)}
        footer={false}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleBulkCreate}>
          <Form.Item label="Tags" name="tag_list">
            <Select mode="tags" placeholder="Enter tags" />
          </Form.Item>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button style={{ marginRight: 8 }} onClick={() => setNewFormVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={tagsLoading}>
                Create Tag
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function InlineEditable({ text, onSave, editing, isSystem }) {
  const [value, setValue] = React.useState(text);
  const inputRef = React.useRef(null);

  React.useEffect(() => { setValue(text); }, [text]);
  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const commit = () => {
    const name = value.trim();
    if (name && name !== text) onSave(name);
  };

  return editing ? (
    <Input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} onBlur={commit} onPressEnter={commit} />
  ) : (
    <Space size="small" wrap>
      <Tag color={isSystem ? 'purple' : 'blue'} style={{ marginRight: 0 }}>{text}</Tag>
      {isSystem && (
        <Tag color="purple" style={{ borderStyle: 'dashed' }}>
          <LockOutlined style={{ marginRight: 4 }} />
          System
        </Tag>
      )}
    </Space>
  );
}
